import { useState } from 'react';
import { useIntl } from 'react-intl';
import { RadioButtonsGroup } from 'sonata-react-components';

import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import './campaignMarketplace.styles.scss';

import type { MediaTypeType } from 'sonata-static-storage/lib/schemas/mediaType/MediaType.types';

const DEFAULT_MESSAGE = {
  hybrid: 'Hybrid (Open & Premium)',
  openRtb: 'Open',
  pmp: 'Premium'
};

type CampaignMarketplaceType = 'mediaType_openRtb' | 'mediaType_pmp' | 'mediaType_hybrid';

type Props = {
  marketPlace: MediaTypeType;
  onCampaignMarketplaceChange: (marketplace: MediaTypeType, prevMarketplace: MediaTypeType) => void;
};

const mediaTypeOrder: Record<string, number> = {
  hybrid: 1,
  openRtb: 0,
  pmp: 2
};

export function CampaignMarketplace({ marketPlace, onCampaignMarketplaceChange }: Props) {
  const [selectedCampaignMarketplace, setSelectedCampaignMarketplace] = useState<CampaignMarketplaceType>(
    `mediaType_${marketPlace}`
  );
  const user = useGlobalStore(getUser);
  const userJSON = user.toJSON();
  const { formatMessage } = useIntl();

  const radioButtonsGroup = [...userJSON.mediaTypes]
    .sort((a, b) => (mediaTypeOrder[a] ?? 99) - (mediaTypeOrder[b] ?? 99))
    .map(mediaType => ({
      id: `mediaType_${mediaType}`,
      text: formatMessage({
        defaultMessage: DEFAULT_MESSAGE[mediaType as keyof typeof DEFAULT_MESSAGE],
        id: `campaignMarketplace.mediaType.${mediaType}`
      }),
      ...(mediaType === 'openRtb' && {
        tooltip: {
          content: formatMessage({
            defaultMessage: 'Switching to "Open" will reset any deals already selected for this campaign.',
            id: 'campaignMarketplace.mediaType.openRtb.tooltip'
          }),
          icon: 'fa-regular fa-circle-info',
          place: 'bottom' as const
        }
      })
    }));

  const handleRadioButtonChange = (newValue: CampaignMarketplaceType) => {
    const newValueNormalized = newValue.replace('mediaType_', '') as MediaTypeType;
    const prevValueNormalized = selectedCampaignMarketplace.replace('mediaType_', '') as MediaTypeType;
    setSelectedCampaignMarketplace(newValue);
    onCampaignMarketplaceChange(newValueNormalized, prevValueNormalized);
  };

  return (
    <div className="campaignMarketplace" id={'campaignMarketplace__box'}>
      <header className="campaignMarketplace__header">
        <div className="campaignMarketplace__title">
          {formatMessage({
            defaultMessage: 'Campaign Marketplace',
            id: 'campaignMarketplace.title'
          })}
        </div>
        <div className="campaignMarketplace__subtitle">
          {formatMessage({
            defaultMessage: 'Choose the type of Marketplace (media buying ecosystem)',
            id: 'campaignMarketplace.subtitle'
          })}
        </div>
      </header>
      <div className="campaignMarketplace__body">
        <div className="campaignMarketplace__radios">
          <RadioButtonsGroup
            className="campaignMarketplace__radiosItem"
            id={'campaignMarketplace__radios'}
            items={radioButtonsGroup}
            onChange={(newValue: string) => handleRadioButtonChange(newValue as CampaignMarketplaceType)}
            value={selectedCampaignMarketplace}
          />
        </div>
      </div>
    </div>
  );
}
